import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useId, useRef, useState } from 'react'
import type { ApiError } from '@/api/errors'
import type { Paginated, SearchHitResponse } from '@/api/types'
import { patients as patientsApi } from '@/api/endpoints'
import { errorMessage, fieldMessages } from '@/api/messages'
import { Avatar } from '@/components/Avatar'
import { Banner } from '@/components/Banner'
import { DateField } from '@/components/DateField'
import { Icon } from '@/components/Icon'
import { Popover } from '@/components/Popover'
import { TextField } from '@/components/TextField'
import { TimeField } from '@/components/TimeField'
import { useToast } from '@/components/Toast'
import { formatIdentityLine } from '@/lib/age'
import { formatVisitDate, todayIso } from '@/lib/dates'
import { useDebounced } from '@/lib/useDebounced'
import { cx } from '@/lib/cx'
import { appointments as api } from './api'

/** Who is being booked. Held as the whole hit so the form can show the name
 *  back without a second lookup. */
type Chosen = Pick<SearchHitResponse, 'id' | 'full_name' | 'op_number' | 'age' | 'sex'>

export function useBookAppointment({
  date,
  patientId,
  onBooked,
}: {
  date?: string | undefined
  patientId?: string | undefined
  onBooked: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const [chosen, setChosen] = useState<Chosen | null>(null)
  const [typed, setTypedRaw] = useState('')
  const [listOpen, setListOpen] = useState(false)
  const [on, setOn] = useState(date ?? todayIso())
  const [at, setAt] = useState('')
  const [location, setLocation] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const query = useDebounced(typed.trim())

  // A patient carried in from "register new patient" is already decided.
  const preselected = useQuery<SearchHitResponse | null, ApiError>({
    queryKey: ['appointment-preselect', patientId],
    queryFn: async ({ signal }) => {
      const p = await patientsApi.get(patientId!, signal)
      return {
        id: p.id,
        full_name: p.full_name,
        op_number: p.op_number,
        age: p.age,
        sex: p.sex,
      } as SearchHitResponse
    },
    enabled: Boolean(patientId) && !chosen,
  })
  const patient = chosen ?? preselected.data ?? null

  const matches = useQuery<Paginated<SearchHitResponse>, ApiError>({
    queryKey: ['appointment-search', query],
    queryFn: ({ signal }) => patientsApi.search(query, 8, null, signal),
    enabled: !patient && query.length >= 2,
  })

  /** What the day already holds — asked for the day being chosen, so the
   *  answer changes as the date does. */
  const day = useQuery<Paginated<{ on: string; locations: string[]; booked: number }>, ApiError>({
    queryKey: ['availability', on],
    queryFn: ({ signal }) => api.availability(on, on, signal),
    enabled: Boolean(on),
  })
  const availability = day.data?.data[0] ?? null

  const save = useMutation({
    mutationFn: () =>
      api.book({
        patient_id: patient!.id,
        scheduled_on: on,
        scheduled_at: at || null,
        // Defaulted from where the doctor is sitting, so the common case is
        // one less thing to type.
        location: (location || availability?.locations[0] || null) as string | null,
        reason: reason.trim() || null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['appointments'] })
      await queryClient.invalidateQueries({ queryKey: ['calendar'] })
      await queryClient.invalidateQueries({ queryKey: ['availability'] })
      toast.show('Appointment booked')
      onBooked()
    },
    onError: (caught) => {
      setError(errorMessage(caught))
      setFieldErrors(fieldMessages(caught as ApiError))
    },
  })

  function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setFieldErrors({})
    if (!patient) {
      setError('Choose who the appointment is for.')
      return
    }
    save.mutate()
  }

  return {
    patient,
    clearPatient: () => {
      setChosen(null)
      setTypedRaw('')
      setListOpen(false)
    },
    choose: (hit: Chosen) => {
      setChosen(hit)
      setListOpen(false)
    },
    typed,
    setTyped: (value: string) => {
      setTypedRaw(value)
      // Typing always reopens it, including after it was dismissed.
      setListOpen(true)
    },
    listOpen,
    closeList: () => setListOpen(false),
    matches,
    on,
    setOn,
    at,
    setAt,
    location,
    setLocation,
    reason,
    setReason,
    availability,
    error,
    fieldErrors,
    save,
    submit,
  }
}

export type BookAppointmentState = ReturnType<typeof useBookAppointment>

export function BookAppointmentFields({
  state,
  onRegisterNew,
}: {
  state: BookAppointmentState
  onRegisterNew: () => void
}) {
  const { patient, availability } = state
  const hits = state.matches.data?.data ?? []
  const anchor = useRef<HTMLDivElement>(null)
  const listId = useId()
  const showResults = !patient && state.listOpen && state.typed.trim().length >= 2

  // Clicking away puts the list down. Anything inside the field or the panel
  // itself is not "away".
  useEffect(() => {
    if (!showResults) return
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Element
      if (anchor.current?.contains(target) || target.closest?.('[data-popover]')) return
      state.closeList()
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [showResults, state])

  return (
    <div className="grid gap-5">
      {state.error && <Banner tone="danger">{state.error}</Banner>}

      <section>
        <h3 className="mb-3 text-2xs font-semibold tracking-wide text-fg-subtle uppercase">
          Who is coming
        </h3>

        {patient ? (
          <div className="flex items-center gap-3 rounded-md border border-line-soft bg-subtle px-4 py-3">
            <span className="min-w-0 flex-1">
              <span className="block font-semibold text-fg">{patient.full_name}</span>
              <span className="block text-sm text-fg-subtle">
                {patient.op_number} · {formatIdentityLine(patient.age, patient.sex)}
              </span>
            </span>
            <button
              type="button"
              onClick={state.clearPatient}
              className="min-h-touch flex-none px-2 text-sm font-medium text-accent"
            >
              Change
            </button>
          </div>
        ) : (
          <>
            <div ref={anchor} className="relative">
              <TextField
                label="Find the patient"
                type="search"
                autoComplete="off"
                autoFocus
                placeholder="OP number, name or phone"
                value={state.typed}
                onChange={(e) => state.setTyped(e.target.value)}
                hint="Type at least two characters."
                // Real combobox semantics, which also keeps Escape from closing
                // the whole dialog while the list is the thing being dismissed.
                role="combobox"
                aria-expanded={showResults}
                aria-controls={showResults ? listId : undefined}
              />
            </div>

            {/* Floated rather than placed in the flow: an inline list pushed the
                date, the availability line and the buttons down the moment
                anyone typed, so the form moved under the cursor. Portalled, so
                the dialog's own scrolling never clips it. */}
            <Popover
              anchor={anchor}
              open={showResults}
              align="stretch"
              className="max-h-[min(60vh,20rem)] overflow-y-auto rounded-lg border border-line bg-raised p-1 shadow-lg"
            >
              <ul
                id={listId}
                role="listbox"
                aria-label="Matching patients"
                className="m-0 list-none p-0 [&>li+li]:border-t [&>li+li]:border-line-faint"
              >
                {state.matches.isLoading ? (
                  <li className="p-4 text-sm text-fg-muted">Searching…</li>
                ) : hits.length === 0 ? (
                  <li className="p-4 text-sm text-fg-muted">
                    No patient found.{' '}
                    <button
                      type="button"
                      className="bg-transparent p-0 text-accent underline underline-offset-2"
                      onClick={onRegisterNew}
                    >
                      Register this patient
                    </button>
                    .
                  </li>
                ) : (
                  hits.map((hit) => (
                    <li key={hit.id} role="option" aria-selected={false}>
                      <button
                        type="button"
                        // Pointer, not click: the field keeps focus, so the list
                        // never has to hand it back.
                        onPointerDown={(event) => {
                          event.preventDefault()
                          state.choose(hit)
                        }}
                        className="flex min-h-row w-full items-center gap-3 rounded-sm bg-surface px-4 py-2 text-left transition-colors duration-150 ease-standard hover:bg-hover"
                      >
                        <Avatar name={hit.full_name} size={36} />
                        <span className="flex min-w-0 flex-1 flex-col gap-px">
                          <span className="flex min-w-0 items-baseline gap-2">
                            <span className="truncate text-base leading-tight font-semibold text-fg">
                              {hit.full_name}
                            </span>
                            <span className="text-xs whitespace-nowrap text-fg-subtle tabular-nums">
                              {hit.op_number}
                            </span>
                          </span>
                          <span className="truncate text-xs leading-snug text-fg-muted">
                            {formatIdentityLine(hit.age, hit.sex)}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </Popover>

            {/* Never registered before? Do it here rather than sending someone
                out to another screen and back. */}
            <button
              type="button"
              onClick={onRegisterNew}
              className="mt-3 inline-flex min-h-touch items-center gap-2 text-sm font-medium text-accent"
            >
              <Icon name="userPlus" size="1rem" />
              Register a new patient
            </button>
          </>
        )}
      </section>

      <section>
        <h3 className="mb-3 text-2xs font-semibold tracking-wide text-fg-subtle uppercase">
          When
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DateField
            label="Date"
            required
            min={todayIso()}
            value={state.on}
            onChange={state.setOn}
            error={state.fieldErrors.scheduled_on}
          />
          <TimeField
            label="Time"
            hint="Optional — leave blank to book the day."
            value={state.at}
            onChange={state.setAt}
          />
        </div>

        {/* The answer to "can they come on Tuesday?" — where you are, and how
            full it already is. */}
        <div
          className={cx(
            'mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md px-3 py-2 text-sm',
            availability && availability.locations.length === 0
              ? 'bg-warning-soft text-warning'
              : 'bg-subtle text-fg-muted',
          )}
          aria-live="polite"
        >
          {!availability ? (
            <span>Checking that day…</span>
          ) : availability.locations.length === 0 ? (
            <>
              <Icon name="alert" size="1rem" />
              <span>
                No travel plan for {formatVisitDate(availability.on)} — the doctor is not sitting
                anywhere that day.
              </span>
            </>
          ) : (
            <>
              <Icon name="pin" size="1rem" />
              <span>
                <strong className="font-semibold text-fg">
                  {availability.locations.join(', ')}
                </strong>{' '}
                on {formatVisitDate(availability.on)} ·{' '}
                {availability.booked === 0
                  ? 'nobody booked yet'
                  : `${availability.booked} already booked`}
              </span>
            </>
          )}
        </div>
      </section>

      <section>
        <h3 className="mb-3 text-2xs font-semibold tracking-wide text-fg-subtle uppercase">
          Details
        </h3>
        <div className="grid gap-4">
          <TextField
            label="Location"
            placeholder={availability?.locations[0] ?? 'Where they should come'}
            hint="Left blank, this takes the place from the travel plan."
            value={state.location}
            onChange={(e) => state.setLocation(e.target.value)}
          />
          <TextField
            label="Reason"
            placeholder="Knee review"
            maxLength={500}
            value={state.reason}
            onChange={(e) => state.setReason(e.target.value)}
          />
        </div>
      </section>
    </div>
  )
}
